import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  COLUMN_TYPES, STATIC_TYPES, ROW_TYPES, POINT_TYPES,
  cellStarts, setCell, setRow, setColumn, renameColumn, applyColumnType,
} from "./gridModel";

const NONE = "__none__";

export default function GridPropertiesPanel({ config, sel, onChange, editable, pointTypes = [], onManagePointTypes }) {
  if (!sel) return <div className="text-xs text-slate-500">Click a cell, a row or a column heading in the table to edit it.</div>;

  // ---- Column settings ----
  if (sel.colIdx != null) {
    const col = config.columns[sel.colIdx];
    if (!col) return null;
    return (
      <div className="space-y-3" data-testid="col-props">
        <div className="text-xs font-semibold text-slate-500">Column {sel.colIdx + 1}</div>
        <div>
          <Label>Column heading</Label>
          <Input className="mt-1 h-8" disabled={!editable} value={col.label || ""}
            onChange={(e) => onChange(renameColumn(config, sel.colIdx, e.target.value))} data-testid="col-prop-label"/>
        </div>
        <div>
          <Label>Column type</Label>
          <Select value={col.col_type || "static_text"} disabled={!editable}
            onValueChange={(v) => onChange(applyColumnType(config, sel.colIdx, v))}>
            <SelectTrigger className="mt-1 h-8" data-testid="col-prop-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              {COLUMN_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[10px] text-slate-400">The heading is what people see. The type controls how the column behaves.</p>
        </div>
        {col.col_type === "dropdown" && (
          <div>
            <Label>Dropdown options (one per line)</Label>
            <Textarea rows={3} className="mt-1" disabled={!editable} value={(col.options || []).join("\n")}
              onChange={(e) => onChange(setColumn(config, sel.colIdx, { options: e.target.value.split("\n") }))} data-testid="col-prop-options"/>
          </div>
        )}
        <div>
          <Label>Width</Label>
          <Input className="mt-1 h-8" type="number" min="1.5" step="0.5" disabled={!editable} value={col.width || 1}
            onChange={(e) => onChange(setColumn(config, sel.colIdx, { width: Math.max(1.5, parseFloat(e.target.value) || 1.5) }))} data-testid="col-prop-width"/>
          <p className="mt-1 text-[10px] text-slate-400">You can also drag the column edge in the table.</p>
        </div>
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox disabled={!editable} checked={!!col.required}
            onCheckedChange={(v) => onChange(setColumn(config, sel.colIdx, { required: !!v }))} data-testid="col-prop-required"/>
          Required when the ITP is filled in
        </label>
      </div>
    );
  }

  // ---- Row + cell settings ----
  const row = config.rows[sel.rowIdx];
  if (!row) return null;
  const cell = sel.cellIdx != null ? row.cells[sel.cellIdx] : null;
  const patchRow = (p) => onChange(setRow(config, sel.rowIdx, p));
  const patchRules = (p) => patchRow({ rules: { ...(row.rules || {}), ...p } });
  const patchCell = (p) => onChange(setCell(config, sel.rowIdx, sel.cellIdx, p));
  const colIdx = cell ? cellStarts(row)[sel.cellIdx] : null;
  const isStaticCell = cell && STATIC_TYPES.has(cell.type || "static_text");
  const currentPt = row.rules?.point_type || "";
  const builtIn = POINT_TYPES.some(([v]) => v === currentPt);
  const custom = pointTypes.find((p) => p.code === currentPt);

  return (
    <div className="space-y-4">
      <div className="space-y-3" data-testid="row-props">
        <div className="text-xs font-semibold text-slate-500">Row {sel.rowIdx + 1}</div>
        <div>
          <Label>Row type</Label>
          <Select value={row.row_type || "item"} disabled={!editable}
            onValueChange={(v) => patchRow({ row_type: v, repeat_on_page_break: v === "header" })}>
            <SelectTrigger className="mt-1 h-8" data-testid="row-prop-type"><SelectValue/></SelectTrigger>
            <SelectContent>{ROW_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {row.row_type === "item" && (
          <div>
            <Label>Point type</Label>
            <Select value={currentPt || NONE} disabled={!editable}
              onValueChange={(v) => patchRules({ point_type: v === NONE ? null : v })}>
              <SelectTrigger className="mt-1 h-8" data-testid="row-prop-point-type"><SelectValue placeholder="None"/></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {POINT_TYPES.map(([v, l, rel]) => <SelectItem key={v} value={v}>{l}{rel ? " (release required)" : ""}</SelectItem>)}
                {pointTypes.map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}{p.release_required ? " (release required)" : ""}</SelectItem>
                ))}
                {currentPt && !builtIn && !custom && <SelectItem value={currentPt}>{currentPt}</SelectItem>}
              </SelectContent>
            </Select>
            <button className="mt-1 text-[11px] text-teal-700 underline-offset-2 hover:underline"
              onClick={onManagePointTypes} data-testid="manage-point-types-btn">
              Manage custom point types…
            </button>
          </div>
        )}
        <div>
          <Label className="text-[11px]">Row shading</Label>
          <div className="mt-1 flex items-center gap-1">
            <input type="color" value={row.shading || "#ffffff"} disabled={!editable}
              onChange={(e) => patchRow({ shading: e.target.value })}
              className="h-7 w-12 cursor-pointer rounded border border-slate-200" data-testid="row-prop-shading"/>
            {row.shading && <Button variant="ghost" size="sm" className="h-6 px-1 text-[10px]" onClick={() => patchRow({ shading: null })}>Clear</Button>}
          </div>
        </div>
      </div>

      {cell && (
        <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="cell-props">
          <div className="text-xs font-semibold text-slate-500">Cell · column {colIdx + 1}</div>
          {isStaticCell ? (
            <div>
              <Label>Text</Label>
              <Textarea rows={3} className="mt-1" disabled={!editable} value={cell.value || ""}
                onChange={(e) => patchCell({ value: e.target.value })} data-testid="cell-prop-value"/>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400">This cell follows its column type and will be completed when the ITP is used.</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs">
              <Checkbox disabled={!editable} checked={!!cell.bold} onCheckedChange={(v) => patchCell({ bold: !!v })} data-testid="cell-prop-bold"/> Bold
            </label>
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-slate-500">Shading</span>
              <input type="color" value={cell.shading || "#ffffff"} disabled={!editable}
                onChange={(e) => patchCell({ shading: e.target.value })}
                className="h-6 w-10 cursor-pointer rounded border border-slate-200" data-testid="cell-prop-shading"/>
              {cell.shading && <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px]" onClick={() => patchCell({ shading: null })} data-testid="cell-prop-clear-shading">Clear</Button>}
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Text alignment</Label>
            <Select value={cell.align || "left"} disabled={!editable} onValueChange={(v) => patchCell({ align: v })}>
              <SelectTrigger className="mt-1 h-7 w-28 text-xs" data-testid="cell-prop-align"><SelectValue/></SelectTrigger>
              <SelectContent>{["left", "center", "right"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
